import React from "react";
import { Button, ClassNameProps, If, makeClassName, Modal, Row, StyleProps } from "@hex-insights/core";
import { Reforge } from "@hex-insights/uploads";
import { Image } from "../../../../Components/Image";
import { reforgeSizeMultiple, truncateDisplayItems, User, UserNameAndImageFragment } from "../../../../Utilities";
import styles from "./styles.module.css";

export type IconUser = Pick<User, "id"> & UserNameAndImageFragment;

export type UserMultiIconProps = Omit<UserIconProps, "user"> & {
	people: UserIconProps["user"][];
	itemClassName?: string;
	itemStyle?: React.CSSProperties;
};

export function UserMultiIcon({
	people,
	title,
	className,
	style,
	size = 32,
	itemClassName,
	itemStyle,
	...props
}: UserMultiIconProps) {
	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(people, 3);
	const isSingleItem = displayedItems.length === 1;

	const mainItemSize = isSingleItem ? size : (size * 2) / 3;
	const smallItemSize = (size * 2) / 5;

	return (
		<div
			title={title}
			className={makeClassName(
				styles["user-multi-icon"],
				isSingleItem ? styles["user-multi-icon--single"] : "",
				className,
			)}
			style={{ "--size": `${size}px`, ...style } as React.CSSProperties}
		>
			{displayedItems.map((user, i) => (
				<UserIcon
					key={i}
					user={user}
					size={i <= 1 ? mainItemSize : smallItemSize}
					className={makeClassName(styles["user-multi-icon__user-icon"], itemClassName)}
					style={itemStyle}
					{...props}
				/>
			))}

			<If condition={anyHidden}>
				<UserAdditionalCountIcon
					count={numHidden}
					size={smallItemSize}
					className={makeClassName(styles["user-multi-icon__user-icon"], itemClassName)}
					style={itemStyle}
				/>
			</If>
		</div>
	);
}

export type UserAdditionalCountIconProps = {
	count: number;
	size?: number;
} & Partial<ClassNameProps & StyleProps>;

export function UserAdditionalCountIcon({ count, size = 32, className, style }: UserAdditionalCountIconProps) {
	const textLength = count.toString().length;
	const textScale = textLength === 1 ? 1 / 2.25 : 1 / (textLength + 1);

	return (
		<div
			className={makeClassName(styles["user-additional-count-icon"], className)}
			style={{ "--size": `${size}px`, ...style } as React.CSSProperties}
		>
			<div
				className={styles["user-additional-count-icon__content"]}
				style={{ "--text-scale": textScale } as React.CSSProperties}
			>
				<span>+{count}</span>
			</div>
		</div>
	);
}

export type UserIconProps = {
	user: IconUser;
	size?: number;
	cssSize?: string;
	title?: string;
} & Partial<ClassNameProps & StyleProps>;

export function UserIcon({ user, size = 32, cssSize, title, className, style }: UserIconProps) {
	const color = getFillColor(user.name);

	const imageEdits = React.useMemo<Reforge.ImageOptions["edits"] | undefined>(() => {
		if (cssSize) {
			return undefined;
		}
		const roundedSize = roundImageSize(size) * reforgeSizeMultiple;
		return { resize: { width: roundedSize, height: roundedSize, fit: "cover" } };
	}, [cssSize, size]);

	const finalSize = cssSize ?? `${size}px`;

	return (
		<Image
			fileKey={user.imageKey}
			contentType={user.imageContentType}
			edits={imageEdits}
			sizing="cover"
			size={finalSize}
			fillColor={color}
			fallbackContent={<UserIconFallback name={user.name} color={color} size={finalSize} />}
			title={title}
			className={makeClassName(styles["user-icon"], className)}
			style={style}
		/>
	);
}

/**
 * Rounds to common sizes for improved image caching.
 */
function roundImageSize(size: number) {
	if (size <= 20) {
		return 20;
	}
	if (size <= 60) {
		return 60;
	}
	if (size <= 100) {
		return 100;
	}
	if (size <= 200) {
		return 200;
	}
	if (size <= 300) {
		return 300;
	}
	return size;
}

type UserIconFallbackProps = {
	name: string;
	color: string;
	size: string;
};

function UserIconFallback({ name, color, size }: UserIconFallbackProps) {
	const displayText = getDisplayText(name);
	const textScale = displayText.length === 1 ? 1 : 0.55;

	return (
		<div
			className={styles["user-icon__fallback"]}
			style={{ "--color": color, "--size": size, "--text-scale": textScale } as React.CSSProperties}
		>
			<span className="no-select">{displayText}</span>
		</div>
	);
}

function getDisplayText(name: string) {
	if (!name || /^\s+$/.test(name)) {
		return "";
	}
	const names = name.split(/\s+/);
	if (names.length === 1) {
		return formatNamePart(names[0]);
	}
	return formatNamePart(names[0]) + formatNamePart(names[names.length - 1]);
}

function formatNamePart(namePart: string) {
	return namePart.charAt(0).toUpperCase();
}

const fillColors = [
	"var(--theme-colors__yellow)",
	"var(--theme-colors__orange)",
	"var(--theme-colors__pink)",
	"var(--theme-colors__teal)",
	"var(--theme-colors__green)",
];

function getFillColor(name: string) {
	if (!name) {
		return fillColors[0];
	}

	const charCode = name.charAt(0).toUpperCase().charCodeAt(0);
	return fillColors[charCode % fillColors.length];
}

export type UserIconWithFullModalProps = UserIconProps & {
	iconClassName?: string;
	iconStyle?: React.CSSProperties;
};

export function UserIconWithFullModal({
	className,
	style,
	iconClassName,
	iconStyle,
	...props
}: UserIconWithFullModalProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button
				onClick={openModal}
				className={makeClassName(styles["user-icon--with-full-modal__button"], className)}
				style={style}
			>
				<UserIcon {...props} className={iconClassName} style={iconStyle} />
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal size="small" onClose={closeModal} className={styles["user-icon--with-full-modal__modal"]}>
					<Modal.Body className={styles["user-icon--with-full-modal__modal__body"]}>
						<Row justify="center" align="center">
							<UserIcon {...props} cssSize="80vmin" className={styles["user-icon--with-full-modal__modal__icon"]} />
						</Row>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}
